import { AdvancedMarker, AdvancedMarkerAnchorPoint, Map, useMap } from '@vis.gl/react-google-maps'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useQuery } from '@apollo/client'
import { isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { googleMapsConfig } from 'config/google-maps'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { GTTripTrackingResponse } from 'gql/types.generated'
import { GET_TRIP_TRACKING, GET_VEHICLE_FOR_TRIP_DETAILS } from 'features/Trips/queries-legacy'
import EmptyState from 'components/v2/EmptyState'
import { BusOutlinedIcon, HelpOutlineIcon, PassengerWithSeatIcon, SpinnerIcon, WindowIcon } from 'components/Icons'
import Tooltip from 'components/v2/Tooltip'
import { GetVehicleForTripDetailsResponse } from 'features/Trips/utils'
import { TripV2 } from 'gql/generated/graphql'
import { adjustMapBounds } from 'utils/google-maps'
import { TripDetailsTimeline } from 'features/Trips/components/TripDrawer/Details/Timeline'
import TabBar from 'components/TabBar'
import { PinIcon } from 'components/Icons/PinIcon'
import { CirclePinIcon } from 'components/Icons/CirclePinIcon'
import { Polyline } from 'components/GoogleMaps/Polyline'

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`
const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #717171;
`

const StyledHelpOutlineIcon = styled(HelpOutlineIcon)`
  fill: #916ee8;
  width: 20px;
  height: 20px;
`

const StyledBusOutlinedIcon = styled(BusOutlinedIcon)`
  fill: #916ee8;
  width: 20px;
  height: 20px;
`
const StyledPassengerWithSeatIcon = styled(PassengerWithSeatIcon)`
  fill: #916ee8;
  width: 20px;
  height: 20px;
`
const StyledWindowIcon = styled(WindowIcon)`
  fill: #916ee8;
  width: 16px;
  height: 16px;
`
const VehicleDetails = styled.div`
  display: flex;
  justify-content: space-between;
`

const VehicleDetailsItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  position: relative;
`

const MapContainer = styled.div`
  border-radius: 16px;
  height: 400px;
  overflow: hidden;
`

const TabBarWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const { defaultCenter, defaultDirectionsRendererOptions, defaultZoom } = googleMapsConfig

const getOriginPosition = ({
  trackingPolylinePath,
  plannedRoutePolylinePath,
  isShowingTrackingRoute
}: {
  trackingPolylinePath: google.maps.LatLngLiteral[]
  plannedRoutePolylinePath: google.maps.LatLngLiteral[]
  isShowingTrackingRoute: boolean
}): google.maps.LatLngLiteral | undefined => {
  if (isShowingTrackingRoute) {
    return trackingPolylinePath.at(0)
  }
  return plannedRoutePolylinePath.at(0)
}

const getDestinationPosition = ({
  trackingPolylinePath,
  plannedRoutePolylinePath,
  isShowingTrackingRoute
}: {
  trackingPolylinePath: google.maps.LatLngLiteral[]
  plannedRoutePolylinePath: google.maps.LatLngLiteral[]
  isShowingTrackingRoute: boolean
}): google.maps.LatLngLiteral | undefined => {
  if (isShowingTrackingRoute) {
    if (trackingPolylinePath.length > 1) {
      return trackingPolylinePath.at(-1)
    }
  } else {
    if (plannedRoutePolylinePath.length > 1) {
      return plannedRoutePolylinePath.at(-1)
    }
  }
  return undefined
}

const ROUTE_OPTIONS = {
  PLANNED_ROUTE: 'PLANNED_ROUTE',
  TRACKING_ROUTE: 'TRACKING_ROUTE'
} as const

const ALL_ROUTES_OPTIONS = [ROUTE_OPTIONS.PLANNED_ROUTE, ROUTE_OPTIONS.TRACKING_ROUTE]

const getDashedLineIcon = ({ strokeOpacity }: { strokeOpacity: number }): google.maps.IconSequence => {
  return {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: strokeOpacity,
      strokeColor: '#D11BDF',
      scale: 4
    },
    offset: '0',
    repeat: '15px'
  }
}

interface ITripDetailsProps {
  trip: TripV2
}

export const TripDetails = ({ trip }: ITripDetailsProps): ReactElement => {
  const tripId = trip?.id
  const vehicleId = trip?.vehicle?.id
  const mapInstance = useMap()
  const [selectedRoutes, setSelectedRoutes] = useState(ALL_ROUTES_OPTIONS)
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsScript()

  const {
    data: tripTrackingData,
    loading: tripTrackingLoading,
    error: tripTrackingError,
    refetch,
    networkStatus
  } = useQuery<{ tripTracking: GTTripTrackingResponse }>(GET_TRIP_TRACKING, {
    fetchPolicy: 'network-only',
    skip: !tripId,
    variables: {
      input: {
        id: tripId
      }
    }
  })

  const { data: vehicleData, loading: loadingVehicleData } = useQuery<{
    tvehicle: GetVehicleForTripDetailsResponse
  }>(GET_VEHICLE_FOR_TRIP_DETAILS, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        id: vehicleId
      }
    },
    skip: !vehicleId
  })

  const trackingPolylinePath = useMemo(() => {
    return (
      tripTrackingData?.tripTracking?.records?.map((record) => ({
        lat: record.latitude,
        lng: record.longitude
      })) || []
    )
  }, [tripTrackingData])

  const plannedRoutePolylinePath = useMemo(() => {
    if (isGoogleMapsLoaded) {
      return google.maps.geometry.encoding.decodePath(trip.route?.polyline?.encoded || '').map((point) => ({
        lat: point.lat(),
        lng: point.lng()
      }))
    }
    return []
  }, [trip.route?.polyline?.encoded, isGoogleMapsLoaded])

  const fullPolylinesPath = useMemo(() => {
    return [...plannedRoutePolylinePath, ...trackingPolylinePath]
  }, [plannedRoutePolylinePath, trackingPolylinePath])

  useEffect(() => {
    if (!mapInstance || isEmpty(fullPolylinesPath)) return

    adjustMapBounds(fullPolylinesPath, mapInstance)
  }, [mapInstance, fullPolylinesPath])

  if (!isGoogleMapsLoaded) {
    return <div />
  }

  if (tripTrackingLoading || networkStatus === NetworkStatus.refetch) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (tripTrackingError) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trip.drawer.details.error"
            defaultMessage="There was an error loading the trip tracking data"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            kind="primary"
            onClick={(): void => {
              refetch()
            }}
          >
            <FormattedMessage id="trip.drawer.details.error.action" defaultMessage="Reload data" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  }

  const isShowingTrackingRoute =
    trackingPolylinePath.length > 1 && selectedRoutes.includes(ROUTE_OPTIONS.TRACKING_ROUTE)

  const isShowingPlannedRoute =
    plannedRoutePolylinePath.length > 1 && selectedRoutes.includes(ROUTE_OPTIONS.PLANNED_ROUTE)
  const originPosition = getOriginPosition({ trackingPolylinePath, plannedRoutePolylinePath, isShowingTrackingRoute })
  const destinationPosition = getDestinationPosition({
    trackingPolylinePath,
    plannedRoutePolylinePath,
    isShowingTrackingRoute
  })

  return (
    <Container>
      <VehicleDetails>
        {loadingVehicleData ? (
          <LoadingContainer>
            <StyledSpinnerIcon />
          </LoadingContainer>
        ) : (
          <>
            {' '}
            <VehicleDetailsItem>
              <StyledBusOutlinedIcon />
              <span>
                {vehicleData?.tvehicle.name}
                {vehicleData?.tvehicle.externalID ? ` (${vehicleData?.tvehicle.externalID})` : ''}
              </span>
              <Tooltip
                content={
                  <FormattedMessage
                    id="trip.drawer.details.vehicleName"
                    defaultMessage="Vehicle name (Partner vehicle id)"
                  />
                }
                position="left"
              >
                <StyledHelpOutlineIcon />
              </Tooltip>
            </VehicleDetailsItem>
            <VehicleDetailsItem>
              <StyledPassengerWithSeatIcon />
              <span>
                {vehicleData?.tvehicle.template.capacity} {'seats'}
              </span>
              <Tooltip
                content={<FormattedMessage id="trip.drawer.details.capacity" defaultMessage="Capacity" />}
                position="left"
              >
                <StyledHelpOutlineIcon />
              </Tooltip>
            </VehicleDetailsItem>
            <VehicleDetailsItem>
              <StyledWindowIcon />
              <span>
                {!isEmpty(vehicleData?.tvehicle.licensePlates) ? vehicleData?.tvehicle.licensePlates[0].number : '-'}
              </span>
              <Tooltip
                content={<FormattedMessage id="trip.drawer.details.licensePlate" defaultMessage="Plates" />}
                position="left"
              >
                <StyledHelpOutlineIcon />
              </Tooltip>
            </VehicleDetailsItem>
          </>
        )}
      </VehicleDetails>
      <MapContainer>
        <Map
          mapId="trip-map"
          defaultZoom={defaultZoom}
          defaultCenter={{
            lat: defaultCenter.LAT,
            lng: defaultCenter.LNG
          }}
          {...googleMapsConfig.defaultOptions}
        >
          {originPosition ? (
            <AdvancedMarker position={originPosition} anchorPoint={AdvancedMarkerAnchorPoint.CENTER}>
              <CirclePinIcon />
            </AdvancedMarker>
          ) : null}
          {destinationPosition ? (
            <AdvancedMarker position={destinationPosition}>
              <PinIcon />
            </AdvancedMarker>
          ) : null}
          {isShowingTrackingRoute && (
            <Polyline
              path={trackingPolylinePath}
              options={{
                geodesic: true,
                ...defaultDirectionsRendererOptions.polylineOptions,
                strokeColor: '#5D59D7'
              }}
            />
          )}
          {isShowingPlannedRoute && (
            <Polyline
              path={plannedRoutePolylinePath}
              options={{
                geodesic: true,
                ...defaultDirectionsRendererOptions.polylineOptions,
                strokeOpacity: 0,
                icons: [getDashedLineIcon({ strokeOpacity: 1 })]
              }}
            />
          )}
        </Map>
      </MapContainer>
      <TabBarWrapper>
        <TabBar.Container>
          <TabBar.Item
            isActive={isEqual(selectedRoutes, ALL_ROUTES_OPTIONS)}
            onClick={(): void => {
              setSelectedRoutes(ALL_ROUTES_OPTIONS)
            }}
          >
            <FormattedMessage id="trip.drawer.details.selectRoute.showAll" defaultMessage="All Shapes" />
          </TabBar.Item>
          <TabBar.Item
            isActive={isEqual(selectedRoutes, [ROUTE_OPTIONS.TRACKING_ROUTE])}
            onClick={(): void => {
              setSelectedRoutes([ROUTE_OPTIONS.TRACKING_ROUTE])
            }}
          >
            <FormattedMessage id="trip.drawer.details.selectRoute.showTrackingRoute" defaultMessage="Current Shape" />
          </TabBar.Item>
          <TabBar.Item
            isActive={isEqual(selectedRoutes, [ROUTE_OPTIONS.PLANNED_ROUTE])}
            onClick={(): void => {
              setSelectedRoutes([ROUTE_OPTIONS.PLANNED_ROUTE])
            }}
          >
            <FormattedMessage id="trip.drawer.details.selectRoute.showPlannedRoute" defaultMessage="Planned Shape" />
          </TabBar.Item>
        </TabBar.Container>
      </TabBarWrapper>
      <TripDetailsTimeline trip={trip} />
    </Container>
  )
}
